'use client';

import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';


interface Panel {
  id: string;
  title: string;
  type: 'odds' | 'scores' | 'signals' | 'agents' | 'market' | 'model';
  visible: boolean;
  size: 'sm' | 'md' | 'lg';
}

const INITIAL_PANELS: Panel[] = [
  { id: 'p1', title: 'LIVE SCORES', type: 'scores', visible: true, size: 'md' },
  { id: 'p2', title: 'SIGNAL FEED', type: 'signals', visible: true, size: 'md' },
  { id: 'p3', title: 'MARKET ODDS', type: 'odds', visible: true, size: 'md' },
  { id: 'p4', title: 'MODEL OUTPUT', type: 'model', visible: true, size: 'md' },
  { id: 'p5', title: 'AGENT STATUS', type: 'agents', visible: true, size: 'sm' },
  { id: 'p6', title: 'MARKET FLOW', type: 'market', visible: true, size: 'sm' },
];

const LIVE_GAMES = [
  { home: 'BOS', away: 'TOR', homeScore: 2, awayScore: 1, period: '2nd', time: '14:23', homeProb: 67.3, status: 'LIVE' },
  { home: 'COL', away: 'VGK', homeScore: 0, awayScore: 0, period: '1st', time: '08:41', homeProb: 58.9, status: 'LIVE' },
  { home: 'TBL', away: 'FLA', homeScore: 3, awayScore: 2, period: '3rd', time: '04:12', homeProb: 71.4, status: 'LIVE' },
];

const SIGNAL_FEED_DATA = [
  { time: '22:41:33', source: 'MarketAgent', msg: 'Sharp money: BOS -1.5 pucks', priority: 'CRITICAL', color: '#ef4444' },
  { time: '22:41:28', source: 'GoalieAgent', msg: 'Swayman .931 SV% last 5 starts', priority: 'HIGH', color: '#f59e0b' },
  { time: '22:41:15', source: 'xG Neural', msg: 'BOS xG: 3.41 vs TOR 2.18', priority: 'HIGH', color: '#3b82f6' },
  { time: '22:40:58', source: 'FatigueAgent', msg: 'TBL fatigue index: 81/100', priority: 'MEDIUM', color: '#a855f7' },
  { time: '22:40:45', source: 'MomentumAgent', msg: 'COL: 4-game win streak active', priority: 'MEDIUM', color: '#10b981' },
  { time: '22:40:30', source: 'InjuryAgent', msg: 'Pastrnak: questionable status', priority: 'HIGH', color: '#ef4444' },
];

const MARKET_FLOW = Array.from({ length: 25 }, (_, i) => ({
  t: i,
  bosLine: -145 + Math.sin(i * 0.3) * 15 + Math.random() * 8,
  torLine: 125 + Math.cos(i * 0.3) * 12 + Math.random() * 6,
}));

const KEYBOARD_SHORTCUTS = [
  { key: 'Ctrl+1', action: 'Toggle Scores Panel' },
  { key: 'Ctrl+2', action: 'Toggle Signal Feed' },
  { key: 'Ctrl+3', action: 'Toggle Market Odds' },
  { key: 'Ctrl+4', action: 'Toggle Model Output' },
  { key: 'Ctrl+D', action: 'Dense Mode' },
  { key: 'Ctrl+F', action: 'Full Screen' },
  { key: 'Ctrl+R', action: 'Refresh All' },
  { key: 'Ctrl+K', action: 'Quick Command' },
];

export default function QuantTerminalClient() {
  const [panels, setPanels] = useState<Panel[]>(INITIAL_PANELS);
  const [denseMode, setDenseMode] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showCommandBar, setShowCommandBar] = useState(false);
  const [command, setCommand] = useState('');
  const [tick, setTick] = useState(0);
  const [commandHistory] = useState(['> SYSTEM READY', '> NHL ANALYTICS PRO v4.0 — QUANT TERMINAL', '> 6 AGENTS ACTIVE', '> 847 SIGNALS PROCESSED TODAY']);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'd') { e.preventDefault(); setDenseMode(d => !d); }
    if (e.ctrlKey && e.key === 'k') { e.preventDefault(); setShowCommandBar(c => !c); }
    if (e.ctrlKey && e.key === '1') { e.preventDefault(); togglePanel('p1'); }
    if (e.ctrlKey && e.key === '2') { e.preventDefault(); togglePanel('p2'); }
    if (e.ctrlKey && e.key === '3') { e.preventDefault(); togglePanel('p3'); }
    if (e.ctrlKey && e.key === '4') { e.preventDefault(); togglePanel('p4'); }
    if (e.key === 'Escape') { setShowCommandBar(false); setShowShortcuts(false); }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const togglePanel = (id: string) => {
    setPanels(prev => prev.map(p => p.id === id ? { ...p, visible: !p.visible } : p));
  };

  const now = new Date();
  const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

  return (
    <div className={`min-h-screen bg-background text-foreground ${denseMode ? 'text-xs' : ''}`}>
      {/* Terminal Header Bar */}
      <div className="bg-card border-b border-border px-4 py-2 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-xs font-mono text-primary font-bold">NHL ANALYTICS PRO — QUANT TERMINAL</span>
          <span className="px-2 py-0.5 text-2xs font-mono bg-primary/10 text-primary border border-primary/20 rounded">PHASE QUANT · ÉTAPE 9</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-green-400">{timeStr}</span>
          <span className="flex items-center gap-1 text-2xs font-mono text-green-400">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />LIVE
          </span>
          <button onClick={() => setShowShortcuts(!showShortcuts)} className="px-2 py-1 text-2xs border border-border text-muted-foreground rounded hover:border-primary/50 font-mono">⌨ SHORTCUTS</button>
          <button onClick={() => setShowCommandBar(!showCommandBar)} className="px-2 py-1 text-2xs border border-primary/30 text-primary rounded hover:bg-primary/10 font-mono">⌘K COMMAND</button>
          <button onClick={() => setDenseMode(!denseMode)} className={`px-2 py-1 text-2xs border rounded font-mono transition-colors ${denseMode ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
            {denseMode ? 'DENSE ●' : 'DENSE ○'}
          </button>
        </div>
      </div>

      {/* Shortcuts Panel */}
      {showShortcuts && (
        <div className="fixed top-14 right-4 z-50 bg-card border border-border rounded-lg p-4 shadow-xl w-64">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-foreground font-mono">KEYBOARD SHORTCUTS</h3>
            <button onClick={() => setShowShortcuts(false)} className="text-muted-foreground hover:text-foreground">×</button>
          </div>
          <div className="space-y-1.5">
            {KEYBOARD_SHORTCUTS.map(s => (
              <div key={s.key} className="flex justify-between items-center">
                <span className="text-2xs text-muted-foreground">{s.action}</span>
                <kbd className="px-1.5 py-0.5 text-2xs bg-background border border-border rounded font-mono text-primary">{s.key}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Command Bar */}
      {showCommandBar && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 w-[500px] bg-card border border-primary/30 rounded-lg shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <span className="text-primary font-mono text-sm">⌘</span>
            <input
              autoFocus
              value={command}
              onChange={e => setCommand(e.target.value)}
              placeholder="Commande rapide... (ex: show BOS, alert TOR, model xg)"
              className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground font-mono"
            />
          </div>
          <div className="p-2 space-y-1">
            {['show BOS stats', 'alert TOR lineup', 'model xg neural', 'market BOS vs TOR', 'simulate COL'].map(cmd => (
              <button key={cmd} onClick={() => { setCommand(cmd); }}
                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-background hover:text-foreground rounded font-mono transition-colors">
                &gt; {cmd}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Panel Toggle Bar */}
      <div className="bg-background border-b border-border px-4 py-1.5 flex items-center gap-2 overflow-x-auto">
        {panels.map(panel => (
          <button key={panel.id} onClick={() => togglePanel(panel.id)}
            className={`flex-shrink-0 px-2 py-1 text-2xs font-mono rounded border transition-colors ${panel.visible ? 'border-primary/40 text-primary bg-primary/5' : 'border-border text-muted-foreground'}`}>
            {panel.visible ? '●' : '○'} {panel.title}
          </button>
        ))}
      </div>

      {/* Terminal Grid */}
      <div className={`p-3 grid gap-3 ${denseMode ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {/* Live Scores Panel */}
        {panels.find(p => p.id === 'p1')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border flex items-center justify-between">
              <span className="text-2xs font-mono text-green-400 font-bold">● LIVE SCORES</span>
              <span className="text-2xs font-mono text-muted-foreground">{timeStr}</span>
            </div>
            <div className="p-3 space-y-2">
              {LIVE_GAMES.map((game, i) => (
                <div key={i} className="p-2 bg-background rounded border border-border">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-foreground font-mono">{game.home}</span>
                      <span className="text-lg font-bold text-primary font-mono">{game.homeScore}–{game.awayScore}</span>
                      <span className="text-xs font-bold text-foreground font-mono">{game.away}</span>
                    </div>
                    <span className="text-2xs text-red-400 font-mono animate-pulse">{game.status}</span>
                  </div>
                  <div className="flex justify-between text-2xs text-muted-foreground font-mono">
                    <span>{game.period} · {game.time}</span>
                    <span className="text-green-400">{game.home} {game.homeProb}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signal Feed Panel */}
        {panels.find(p => p.id === 'p2')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border flex items-center justify-between">
              <span className="text-2xs font-mono text-yellow-400 font-bold">⚡ SIGNAL FEED</span>
              <span className="text-2xs font-mono text-muted-foreground">{SIGNAL_FEED_DATA.length} signals</span>
            </div>
            <div className="p-2 space-y-1 max-h-52 overflow-y-auto">
              {SIGNAL_FEED_DATA.map((sig, i) => (
                <div key={i} className="flex gap-2 p-1.5 bg-background rounded border border-border text-2xs font-mono">
                  <span className="text-muted-foreground w-14 flex-shrink-0">{sig.time.split(':').slice(1).join(':')}</span>
                  <span style={{ color: sig.color }} className="flex-shrink-0 w-20 truncate">{sig.source}</span>
                  <span className="text-foreground flex-1 truncate">{sig.msg}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Market Odds Panel */}
        {panels.find(p => p.id === 'p3')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border flex items-center justify-between">
              <span className="text-2xs font-mono text-blue-400 font-bold">📊 MARKET ODDS</span>
              <span className="text-2xs font-mono text-muted-foreground">LIVE</span>
            </div>
            <div className="p-3">
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={MARKET_FLOW.slice(-15)}>
                  <XAxis dataKey="t" hide />
                  <YAxis hide />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 4, fontSize: 10 }} />
                  <Line type="monotone" dataKey="bosLine" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="BOS ML" />
                  <Line type="monotone" dataKey="torLine" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="TOR ML" />
                </LineChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[{ team: 'BOS', ml: '-148', spread: '-1.5', ou: 'O 5.5' }, { team: 'TOR', ml: '+124', spread: '+1.5', ou: 'U 5.5' }].map(t => (
                  <div key={t.team} className="col-span-1">
                    <div className="text-2xs font-mono text-muted-foreground">{t.team}</div>
                    <div className="text-xs font-bold font-mono text-foreground">{t.ml}</div>
                    <div className="text-2xs font-mono text-muted-foreground">{t.spread} · {t.ou}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Model Output Panel */}
        {panels.find(p => p.id === 'p4')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border">
              <span className="text-2xs font-mono text-purple-400 font-bold">🧠 MODEL OUTPUT</span>
            </div>
            <div className="p-3 space-y-2">
              {[
                { model: 'xG Neural', game: 'BOS vs TOR', pred: 'BOS', conf: 73.2, color: '#10b981' },
                { model: 'Elo Advanced', game: 'COL vs VGK', pred: 'COL', conf: 68.9, color: '#3b82f6' },
                { model: 'Ensemble', game: 'TBL vs FLA', pred: 'TBL', conf: 71.4, color: '#a855f7' },
              ].map((m, i) => (
                <div key={i} className="p-2 bg-background rounded border border-border">
                  <div className="flex justify-between text-2xs font-mono mb-1">
                    <span className="text-muted-foreground">{m.model}</span>
                    <span className="text-muted-foreground">{m.game}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold font-mono" style={{ color: m.color }}>{m.pred}</span>
                    <span className="text-xs font-mono text-foreground">{m.conf}%</span>
                  </div>
                  <div className="mt-1 h-1 bg-border rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${m.conf}%`, backgroundColor: m.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agent Status Panel */}
        {panels.find(p => p.id === 'p5')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border">
              <span className="text-2xs font-mono text-orange-400 font-bold">🤖 AGENT STATUS</span>
            </div>
            <div className="p-2 space-y-1">
              {[
                { name: 'InjuryAgent', status: 'ALERT', color: '#ef4444' },
                { name: 'GoalieAgent', status: 'ACTIVE', color: '#10b981' },
                { name: 'MarketAgent', status: 'ALERT', color: '#ef4444' },
                { name: 'MomentumAgent', status: 'SCAN', color: '#3b82f6' },
                { name: 'FatigueAgent', status: 'ACTIVE', color: '#10b981' },
                { name: 'SimAgent', status: 'ACTIVE', color: '#10b981' },
              ].map(a => (
                <div key={a.name} className="flex justify-between items-center text-2xs font-mono">
                  <span className="text-muted-foreground">{a.name}</span>
                  <span style={{ color: a.color }}>● {a.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Market Flow Panel */}
        {panels.find(p => p.id === 'p6')?.visible && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-background border-b border-border">
              <span className="text-2xs font-mono text-cyan-400 font-bold">💹 MARKET FLOW</span>
            </div>
            <div className="p-2">
              <div className="space-y-1.5">
                {[
                  { label: 'Sharp %', value: '67%', color: '#10b981' },
                  { label: 'Public %', value: '33%', color: '#64748b' },
                  { label: 'Line Move', value: '-2.5', color: '#ef4444' },
                  { label: 'Volume', value: '↑ HIGH', color: '#f59e0b' },
                ].map(m => (
                  <div key={m.label} className="flex justify-between text-2xs font-mono">
                    <span className="text-muted-foreground">{m.label}</span>
                    <span style={{ color: m.color }} className="font-bold">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Terminal Console */}
      <div className="mx-3 mb-3 bg-black border border-green-400/20 rounded-lg p-3 font-mono">
        <div className="text-2xs text-green-400/60 mb-2">TERMINAL CONSOLE</div>
        {commandHistory.map((line, i) => (
          <div key={i} className="text-2xs text-green-400">{line}</div>
        ))}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-2xs text-green-400">{'>'}</span>
          <span className="text-2xs text-green-400 animate-pulse">█</span>
        </div>
      </div>
    </div>
  );
}
